// src/store/authStore.ts
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import * as authApi from '../api/auth'

// Note: DB-specific types removed; using API DTOs

// Application user interface
export interface User {
  id: string
  email: string
  name: string
  roleId: string
  roleName?: string
  // Back-compat convenience fields
  role?: string
  positionId: string
  positionName?: string
  position?: string
  departmentId: string
  departmentName?: string
  department?: string
  employeeId: string
  isActive: boolean
  joinDate: string
  status: string
  totalVacDays: number
  remainingVacDays: number
  vacationAccruedMonths?: number
  vacationLastAccrualDate?: string | null
  phone?: string | null
  emergencyContact?: string | null
  address?: string | null
  lastLogin?: string | null
  createdAt: string
  updatedAt: string
  permissions?: string[]
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  register: (email: string, password: string, name: string) => Promise<void>
  updateProfile: (updates: Partial<User>) => Promise<void>
  checkAuth: () => Promise<void>
  refreshUser: () => Promise<void>
  clearError: () => void
  getUser: () => User | null
}

// Helper to map API user to application user (already camelCase)
function slugify(value?: string) {
  return (value || '').toLowerCase().replace(/\s+/g, '-');
}

function mapApiUser(u: authApi.AuthUser, permissions?: string[]): User {
  return {
    ...u,
    role: u.roleName,
    // Use slug for gating while keeping original for display
    position: slugify(u.positionName),
    department: u.departmentName,
    permissions: permissions || [],
    vacationAccruedMonths: u.vacationAccruedMonths,
    vacationLastAccrualDate: u.vacationLastAccrualDate,
  }
}

// Storage version - increment this when auth structure changes
const AUTH_STORAGE_VERSION = 2;

export const authStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      getUser: () => get().user,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.login(email, password)
          const { token, user, permissions } = response
          localStorage.setItem('auth-token', token)
          const mappedUser = mapApiUser(user, permissions?.permissions || [])

          set({ user: mappedUser, isAuthenticated: true, isLoading: false, error: null })
        } catch (error) {
          console.error('Login error:', error)
          const errorMessage = error instanceof Error ? error.message : 'Login failed'
          set({ user: null, isAuthenticated: false, isLoading: false, error: errorMessage })
          throw error
        }
      },

      logout: async () => {
        set({ isLoading: true })
        try {
          await authApi.logout()
          localStorage.removeItem('auth-token')
          set({ user: null, isAuthenticated: false, isLoading: false, error: null })
        } catch (error) {
          console.error('Logout error:', error)
          const errorMessage = error instanceof Error ? error.message : 'Logout failed'
          set({ isLoading: false, error: errorMessage })
          throw error
        }
      },

      register: async (email: string, password: string, name: string) => {
        set({ isLoading: true, error: null })
        try {
          const { token, user } = await authApi.register({ email, password, name })
          localStorage.setItem('auth-token', token)
          const mappedUser = mapApiUser(user)

          set({ user: mappedUser, isAuthenticated: true, isLoading: false, error: null })
        } catch (error) {
          console.error('Registration error:', error)
          const errorMessage = error instanceof Error ? error.message : 'Registration failed'
          set({ user: null, isAuthenticated: false, isLoading: false, error: errorMessage })
          throw error
        }
      },

      updateProfile: async (updates: Partial<User>) => {
        set({ isLoading: true, error: null })
        try {
          const updated = await authApi.updateMe(updates)
          const mappedUser = mapApiUser(updated)
          set({ user: mappedUser, isLoading: false, error: null })
        } catch (error) {
          console.error('Profile update error:', error)
          const errorMessage = error instanceof Error ? error.message : 'Profile update failed'
          set({ isLoading: false, error: errorMessage })
          throw error
        }
      },

      checkAuth: async () => {
        set({ isLoading: true })
        try {
          const me = await authApi.getMe()
          if (me) {
            const mappedUser = mapApiUser(me)
            set({ user: mappedUser, isAuthenticated: true, isLoading: false, error: null })
          } else {
            set({ user: null, isAuthenticated: false, isLoading: false, error: null })
          }
        } catch (error) {
          console.error('Auth check error:', error)
          set({ user: null, isAuthenticated: false, isLoading: false, error: null })
        }
      },

      refreshUser: async () => {
        try {
          const me = await authApi.getMe()
          if (me) set({ user: mapApiUser(me) })
        } catch (_e) {
          // Swallow errors on background refresh to avoid unhandled rejections
        }
      },

      clearError: () => set({ error: null })
    }),
    {
      name: 'auth-storage',
      version: AUTH_STORAGE_VERSION,
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      }),
      migrate: (persistedState: any, version: number) => {
        // If version mismatch, clear old data and force re-login
        if (version !== AUTH_STORAGE_VERSION) {
          localStorage.removeItem('auth-token');
          return {
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
          };
        }
        return persistedState as AuthState;
      }
    }
  )
)

// Export the store instance for direct access
export const useAuthStore = authStore

// Helper hooks for permissions and roles
export const usePermission = (permissionName: string): boolean => {
  const user = useAuthStore((state) => state.user)
  if (!user) return false

  // Super admin has all permissions
  if (user.roleName === 'super-admin') return true

  // Check if user has the specific permission
  if (user.permissions && user.permissions.includes(permissionName)) {
    return true
  }

  // Check for wildcard permissions
  const [resource, action] = permissionName.split(':')
  if (user.permissions) {
    if (user.permissions.includes(`${resource}:*`)) return true
    if (user.permissions.includes(`all:${action}`)) return true
    if (user.permissions.includes('all:*')) return true
  }

  return false
}

export const useRole = (): string | null => {
  const user = useAuthStore((state) => state.user)
  return user?.roleName || null
}

export const useDepartment = (): string | null => {
  const user = useAuthStore((state) => state.user)
  return user?.departmentName || null
}
